<template>
  <div class="dashboard">
    <div class="page-header">
      <div>
        <h1 class="page-title">{{ greeting }}, {{ userName }}</h1>
        <p class="page-sub">Here's what's on your plate today</p>
      </div>
      <button class="btn btn-primary" @click="showForm = true">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
        New Task
      </button>
    </div>

    <!-- Stats -->
    <div class="stats-grid">
      <div class="stat-card">
        <div class="stat-icon open-icon">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
        </div>
        <div>
          <div class="stat-value">{{ stats.open }}</div>
          <div class="stat-label">Open</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon progress-icon">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>
        </div>
        <div>
          <div class="stat-value">{{ stats.in_progress }}</div>
          <div class="stat-label">In Progress</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon done-icon">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
        </div>
        <div>
          <div class="stat-value">{{ stats.done }}</div>
          <div class="stat-label">Done</div>
        </div>
      </div>
      <div class="stat-card critical-card">
        <div class="stat-icon critical-icon">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
        </div>
        <div>
          <div class="stat-value">{{ stats.critical }}</div>
          <div class="stat-label">Critical</div>
        </div>
      </div>
    </div>

    <!-- Recent Tasks -->
    <div class="section-header">
      <h2 class="section-title">Recent Tasks</h2>
      <router-link to="/tasks" class="btn btn-ghost btn-sm">View all →</router-link>
    </div>

    <div v-if="loading" class="spinner"></div>

    <div v-else-if="tasks.length === 0" class="empty-state">
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" opacity=".3"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg>
      <p>No tasks yet. Create your first one!</p>
    </div>

    <div v-else class="tasks-grid">
      <TaskCard
        v-for="task in tasks"
        :key="task.id"
        :task="task"
        @edit="editTask"
        @complete="completeTask"
        @reopen="reopenTask"
        @archive="archiveTask"
        @timer-update="onTimerUpdate"
      />
    </div>

    <TaskFormModal
      v-if="showForm"
      :task="editingTask"
      :projects="projects"
      @close="closeForm"
      @saved="onSaved"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import TaskCard      from '@/components/TaskCard.vue'
import TaskFormModal from '@/components/TaskFormModal.vue'
import { useAuth }   from '@/composables/useAuth'
import api from '@/api'

export default {
  name: 'DashboardView',
  components: { TaskCard, TaskFormModal },
  setup() {
    const { currentUser } = useAuth()
    const tasks    = ref([])
    const projects = ref([])
    const loading  = ref(true)
    const showForm = ref(false)
    const editingTask = ref(null)

    const userName = computed(() => currentUser()?.name?.split(' ')[0] || 'there')
    const greeting = computed(() => {
      const h = new Date().getHours()
      if (h < 12) return 'Good morning'
      if (h < 17) return 'Good afternoon'
      return 'Good evening'
    })

    const stats = computed(() => ({
      open:        tasks.value.filter(t => t.status === 'open').length,
      in_progress: tasks.value.filter(t => t.status === 'in_progress').length,
      done:        tasks.value.filter(t => t.status === 'done').length,
      critical:    tasks.value.filter(t => t.urgency === 'critical' && t.status !== 'done').length,
    }))

    async function load() {
      loading.value = true
      try {
        const [tr, pr] = await Promise.all([
          api.getTasks({ mine: 1 }),
          api.getProjects(),
        ])
        tasks.value    = (tr.data || []).slice(0, 12)
        projects.value = pr.data || []
      } catch {} finally {
        loading.value = false
      }
    }

    async function completeTask(task) {
      try {
        await api.completeTask(task.id)
        await load()
      } catch {}
    }

    async function reopenTask(task) {
      try {
        await api.reopenTask(task.id)
        await load()
      } catch {}
    }

    async function archiveTask(task) {
      if (!confirm(`Archive "${task.title}"? It will move to the archive.`)) return
      try {
        await api.archiveTask(task.id)
        tasks.value = tasks.value.filter(t => t.id !== task.id)
      } catch {}
    }

    function editTask(task) {
      editingTask.value = task
      showForm.value = true
    }

    function closeForm() {
      showForm.value = false
      editingTask.value = null
    }

    function onSaved() { load() }

    function onTimerUpdate(payload) {
      const t = tasks.value.find(t => t.id === payload.taskId)
      if (t) {
        t.timer_seconds    = payload.timer_seconds
        t.timer_started_at = payload.timer_started_at
      }
    }

    onMounted(load)

    return {
      tasks, projects, loading, showForm, editingTask,
      userName, greeting, stats,
      completeTask, reopenTask, archiveTask, editTask, closeForm, onSaved, onTimerUpdate,
    }
  },
}
</script>

<style scoped>
.page-sub { color: var(--muted); font-size: 13px; margin-top: 2px; }

.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 32px;
}
.stat-card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
}
.critical-card { border-color: rgba(239,68,68,.4); background: rgba(239,68,68,.05); }
.stat-icon {
  width: 44px; height: 44px;
  border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.open-icon     { background: rgba(100,116,139,.15); color: var(--muted); }
.progress-icon { background: rgba(99,102,241,.15);  color: var(--primary); }
.done-icon     { background: rgba(34,197,94,.15);   color: var(--success); }
.critical-icon { background: rgba(239,68,68,.15);   color: var(--danger); }
.stat-value { font-size: 28px; font-weight: 700; line-height: 1; }
.stat-label { font-size: 12px; color: var(--muted); margin-top: 4px; }

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}
.section-title { font-size: 16px; font-weight: 600; }

.tasks-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 14px;
}

@media (max-width: 900px) {
  .stats-grid { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 600px) {
  .stats-grid { grid-template-columns: 1fr 1fr; }
  .tasks-grid { grid-template-columns: 1fr; }
}
</style>
